/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

export interface ActionWizzardTargetableAction {
   target: string,
}

export interface WizzardAutoActionKeypress extends ActionWizzardTargetableAction {
   type: "keypress",
   data: string,
   speed?: number
}

export interface WizzardAutoActionDblClick extends ActionWizzardTargetableAction {
   type: "dblclick"
}

export interface WizzardAutoActionHighlight extends ActionWizzardTargetableAction {
   type: "highlight",
   padding?: string,
   scrollToView?: boolean
}

export interface WizzardAutoActionDelay {
   type: "delay",
   time: number
}

export type WizzardAutoAction = WizzardAutoActionKeypress | WizzardAutoActionDblClick | WizzardAutoActionDelay | WizzardAutoActionHighlight;

export interface WizzardPage {
   caption: string,
   content: React.ReactNode,
   highlight: string,
   actions: WizzardAutoAction[],
   padding?: string;
   preventScroll?: boolean;
}


const wizzardPages: WizzardPage[] = [

   {
      caption: "Welcome to CVE Radar",
      content: (
         <>
            <p><strong>CVE Radar</strong> is solution designed to identify security vulnerabilities discovered and published for concrete information technology systems, software and packages in a quick, efficient and a simple way.</p>
            <p>It allows to significantly simplify and increase effectivness of the information security vulnerability management process including Zero Day Vulnerability alerting, as well as it can help to organizations and security specialists to simplify fullfillment of compliance frameworks requirements.</p>
            <p>Master data source for CVE Radar is <strong>National Vulnerability Database</strong> documenting vulnerabilities as Common Vulnerability Envumeration (CVE) records. This database is published on <a href="https://nvd.nist.gov" target="_blank" rel="noreferrer"> nvd.nist.gov</a> portal.</p>
            <p>This guide shows how to use <strong>CVE Radar</strong> to reduce manual work with identification of security vulnerabilities on IT systems, software and packages (<strong>Assets</strong>) of interrest.</p>
         </>
      ),
      highlight: "",
      actions: [],
   },

   {
      caption: "Company",
      content: (
         <>
            <p>
               CVE Radar allows a single user to work with data of multiple companies, ofcourse only in case the appropriate permsissions has been assigned
               to the user account.
            </p>

            <p>
               When the user signs in to the CVE radar for the first time and <strong>has not been invited</strong> to any company the initial company
               of the user is created automatically. In this case, the <strong>domain part</strong> of an user email is used as the company initial name. The company
               name can be changed in the company settings dialog later.
            </p>

            <p>
               If the user <strong>has been invited</strong> to the CVE Radar from a company already existing in the system, the company name will be listed in the
               <strong> Company Selection Box</strong> located in the CVE Radar navigation bar on the top.
            </p>

            <p>
               All configurations, settings and data displayed on the screen or changed by the user <strong>always relates</strong> only to the company selected in the company selection box.
               These data are shared with other users assigned to the same company (members) depndent on the role and permission assignment.
            </p>

            <p>
               In case the user is member of more companies it is <strong>highly recomended</strong> to always check which company is selected in the select box
               before any other actions (such as company or user settings change or asset groups settings modifications) are executed, otherwise the security breach
               in form of a company data leak can occur. This can be prevented by setting of security policies discussed later.
            </p>

         </>
      ),
      highlight: ".navbar-item-company",
      actions: [],
   },

   {
      caption: "Select Assets",
      content: (
         <>
         <p>To be able to decide wheter a vulnerability applies to particualr hardware, system or application (Asset), it must be identified first.</p>
         <p>CVE Radar manages a database of assets for which a vulnerability has been identified in past. This database is based on the official <a href="https://nvd.nist.gov" target="_blank" rel="noreferrer">NVD CPE dictionary</a></p>
         <p>To do so, first select Asset's Vendor and Product name from CVE Radar's datbase.</p>
         <p>Both Vendor and Product must be selected.</p>
         </>
      ),
      highlight: ".available-filters",
      actions: [],
   },

   {
      caption: "Select Assets - Search Vendor / Product",
      content: (
         <>
            <p>Start searching in CVE Radar's databse, by entering Vendor's name of your Asset. CVE Radar will whisper you available vendor names.</p>
            <p>Analoguous to serching vendor, CVE Radar will propose similar names while you type name of hardware, operating system and application.</p>

         </>
      ),
      highlight: ".available-filters .vendor .tk-input-drop-down-vpv",
      actions: [
         {
            type: "keypress",
            target: ".available-filters .vendor .tk-input-drop-down-vpv input",
            data: "Microsoft",
            speed: 100
         },
         {
            type: "delay",
            time: 1500
         },
         {
            type: "highlight",
            target: ".available-filters .product .tk-input-drop-down-vpv",
            padding: "-0.25em",
            scrollToView: true
         },
         {
            type: "keypress",
            target: ".available-filters .product .tk-input-drop-down-vpv input",
            data: "Windows 10",
            speed: 100
         },
         {
            type: "delay",
            time: 1500
         }
      ],
      padding: "-0.25em",
   },
   {
      caption: "Select Assets - List of Available Product Versions",
      content: (
         <>
         <p>After you enter vendor and product name, CVE Radar will show you table with all versions available in it's database.</p>
         <p>Product may have several different Versions, Editions, Languages, etc.</p>
        </>
      ),
      highlight: ".available-asset-filters-component > .container",
      actions: [],
      padding: "-0.25em",
   },


   {
      caption: "Select Assets - Versions Selecting",
      content: (
         <>
			 <p>You can select any of those parameters or their combination to precise your query and therefore query results by double clicking any column.</p>
			 <p>Also, multiple different products can be added to the selected asset list in order to obtain list of CVEs for selected products and their versions at once</p>
          <p>To add a specific product property to the selected asset list <b>double click</b> the appropriate property within the list of found product properties.</p>
         </>
      ),
      highlight: ".available-asset-filters-component .tk-column-grid .column .item",
      actions: [
         {
            type: "dblclick",
            target: ".available-asset-filters-component .tk-column-grid .column .item"
         },
         {
            type: "delay",
            time: 1500
         }
      ],
      padding: "-0.25em",
      preventScroll: true
   },

   {
      caption: "Selected Assets - Added Asset",
      content: (
         <>
            <p>Added filter can be seen in the <b>Selected Asset</b> widged now.</p> <p>Moreover, the <b>CVE List</b> gets filtered accordingly.</p>
         </>
      ),
      highlight: ".product-filter-list-component-default",
      actions: [],
      padding: "-0.25em",
      preventScroll: true
   },

/*<p>Later on, when saved, the Selected Asset list can be used to distribute alerts about new vulnerabilities to Asset responsibles.</p>*/

   {
      caption: "Selected Assets",
      content: (
         <>
            <p>Selected assets list is used to filter the CVE list by the criteria defined in previous step.</p>
            <p>Any added product or its property can be removed from the selected Asset by clicking on the rleated remove icon or double clicking the Asset or Asset's propetry.</p>
         </>
      ),
      highlight: ".selected-filters",
      actions: [],
   },

   {
      caption: "Selected Assets - Asset Groups creating, using and editing.",
      content: (
         <>
            <p>
               Selected Assets can be saved to existing or new group by clicking on Save or Save As icons next to the group selector.
            </p>
         </>
      ),
      highlight: ".selected-filters .tk_widget-header .controls",
      actions: [],
   },

   {
      caption: "CVE List",
      content: (
         <>
            <p>The most important part of the application is the <strong>CVE List</strong> widget showing the list of publicly known vulnerabilities. All vulnerabilities recorded in the NVD database can be viewed, searched and/or filtered.</p>
            <p>When there are no Assets in the Selected Assets list, CVE Radar shows all existing vulnerabilities within timframe selected in CVE List.</p>
            <p>When there are Assets in the Selected Asset list, changes in values entered into Vendor and Product fields do not affer displayed list of CVEs.</p>
            <p></p>
            <p>Please note that CVE Radar can get maximally 10.000 CVE records at once.</p>
         </>
      ),
      highlight: ".cve-list",
      actions: [],
   },

   {
      caption: "CVE List - Date range filtering",
      content: (
         <>
            <p>To filter <strong>CVE List</strong> using the date range choose the appropriate option from the <strong>date range</strong> combo box or select <strong>start</strong> and <strong>end</strong> dates of your choice.</p>
         </>
      ),
      highlight: ".tk-widget-cve-list .tk_widget-header div:nth-child(2)",
      actions: [],
      padding: "-0.25em",
   },

   {
      caption: "CVE Detail",
      content: (
         <>
            <p>All available details related to the publicly known vulnerability selected in the <strong>CVE list</strong> can be viewed in the <strong>CVE detail</strong> widget, which is splitted to multiple logically separated sections.</p>
         </>
      ),
      highlight: ".cve-detail",
      actions: [],
   },

/*
            <p>Each user must have User or Admin role.</p>
            <p>User role can edit Company Details, Users & User Groups and Asset Groups.</p>
            <p>Admin role can edit Company Details, Users & User Groups and Asset Groups.</p>

            <p>Users &amps; Groups</p>
            <p>Your Company acocunt may have more more users that can manage Comapny settings and Asset Groups.</p>
*/

   {
      caption: "Company Settings",
      content: (
         <>
            <p>In the top right side of the top navigation bar is Company Settings icon to menu for Company details and Users &amps; Groups in your Company.</p>
            <p>Company details</p>
            <p>In Company details you can edit company contact information and invoicing information.</p>
            <p>Users &amps; Groups</p>
            <p>Uou can add or edit Users in your Comapny and send them email invitation to participate on security vulentabilities monitorign process in your team or organoization.</p>
         </>
      ),
      highlight: ".navbar-item-icon-settings",
      actions: [],
   },

   {
      caption: "Logout",
      content: (
         <>
            <p>Logout from CVE Radat using logout option in the User Profile menu presented under your email located in the right side of the top navigation bar.</p>
         </>
      ),
      highlight: ".navbar-item-icon-user",
      actions: [],
   },

   {
      caption: "Show this wizard again",
      content: (
         <>
            <p>To show this wizard again, clisk on the help icon in the most right side of the top navigation bar.</p>
         </>
      ),
      highlight: ".navbar-item-icon-help",
      actions: [],
   },

]


export default wizzardPages;